import React, { Component } from 'react';
import gql from 'graphql-tag';
import cloneDeep from 'lodash/cloneDeep';
import memoizeOne from 'memoize-one';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import ActiveParticipantDisplay from 'components/ActiveParticipantDisplay';
import GamePlayerList from 'components/GamePlayerList';
import MessageForm, { MESSAGE_TYPE_CHAT } from 'components/MessageForm';
import ProfileIcon from 'components/ProfileIcon';
import { fromIso9075DateString } from 'utils/dates';
import { padNumber } from 'utils/formatting';
import { getUserData } from 'utils/graphQLFunctions';
import fragments from 'utils/graphql/fragments';
import insertionSortUnique from 'utils/insertionSortUnique';
import { isMobileDevice_DEPRECATED } from 'utils/mode';
import { cmDisplayName } from 'utils/roles';
import { chatMessageShape, errorShape } from 'utils/shapes';
import './GameChat.css';

const MAX_CHAT_MESSAGES = 500;
const CHAT_SUBMIT_DELAY_MS = 1000;
const SCROLL_LOCK_MIN_DIST_PX = 2;

class GameChat extends Component {
  constructor(props) {
    super(props);

    this.state = {
      message: '',
      showEmojiPicker: false
    };

    this.renderMessageListMemo = memoizeOne(this.renderMessageList.bind(this));

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.scrollToBottom = this.scrollToBottom.bind(this);
    this.toggleEmojiPicker = this.toggleEmojiPicker.bind(this);
    this.addEmoji = this.addEmoji.bind(this);
    this.chatSubmitTimer = 0;

    this.unsubscribeFromChatUpdates = () => {};
  }

  handleChange(event) {
    this.setState({
      message: event.target.value
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Enter') {
      this.handleSubmit(event);

      if (isMobileDevice_DEPRECATED()) {
        // Automatically close the soft-keyboard on mobile
        event.target.blur();
      }
    }
  }

  handleSubmit(event) {
    event.preventDefault();

    if (Date.now() < this.chatSubmitTimer) {
      return;
    }

    const message = this.state.message.replace(/[\n\r]/g, '').trim();
    if (message.length > 0) {
      this.props.makeChatMessage(message);
      this.chatSubmitTimer = Date.now() + CHAT_SUBMIT_DELAY_MS;
    }

    this.setState({ message: '' });
  }

  componentDidMount() {
    this.unsubscribeFromChatUpdates = this.props.subscribeToChatUpdates();
    this.scrollToBottom();
  }

  componentWillUnmount() {
    this.unsubscribeFromChatUpdates();
    this.unsubscribeFromChatUpdates = () => {};
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.message !== nextState.message ||
      this.props.chatData !== nextProps.chatData ||
      this.props.gameData !== nextProps.gameData ||
      this.state.showEmojiPicker !== nextState.showEmojiPicker
    );
  }

  componentWillUpdate() {
    const c = this.listContainer;
    const dist = c.scrollHeight - c.scrollTop - c.clientHeight;
    this.shouldScrollBottom = dist <= SCROLL_LOCK_MIN_DIST_PX;
  }

  componentDidUpdate() {
    if (this.shouldScrollBottom) {
      this.scrollToBottom();
    }
  }

  scrollToBottom() {
    this.listContainer.scrollTop = this.listContainer.scrollHeight;
  }

  toggleEmojiPicker() {
    const isEmoji = this.state.showEmojiPicker;
    this.setState({
      showEmojiPicker: !isEmoji
    });
  }

  addEmoji(emoji) {
    const { message } = this.state;
    const text = `${message}${emoji.native}`;

    this.setState({
      message: text
    });
  }

  renderChatMessage(msg, date) {
    const selfChatMessage =
      this.props.userData.user.displayName === msg.author.displayName;
    return (
      <li
        className={`chat-message ${
          selfChatMessage ? 'right-align' : 'left-align'
        }`}
        key={`msg-${msg.id}`}
      >
        {selfChatMessage ? (
          <div className="self-chat-message">
            <div className="text-content">
              <span>{msg.message}</span>
            </div>
            {!!this.props.showTime && this.renderMessageTime(date)}
          </div>
        ) : (
          <div className="participant-chat-message">
            <div className="chat-author-section">
              <span className="chat-author-name notranslate">
                {cmDisplayName(msg.author)}{' '}
              </span>
            </div>
            <div className="chat-text-section">
              <div className="user-profile-section">
                <ProfileIcon avatarData={msg.author.avatarData} size={36} />
              </div>
              <div className="text-time-section">
                <div className="text-content">
                  <span>{msg.message}</span>
                </div>
                {!!this.props.showTime && this.renderMessageTime(date)}
              </div>
            </div>
          </div>
        )}
      </li>
    );
  }

  renderMessage(msg, date) {
    return (
      <li className="chat-message" key={`msg-${msg.id}`}>
        {!!this.props.showTime && this.renderMessageTime(date)}
        <span className="chat-author-name notranslate">
          {cmDisplayName(msg.author)}{' '}
        </span>
        <span className="chatBubble">{msg.message}</span>
      </li>
    );
  }

  renderMessageTime(date) {
    const hours24 = date.getHours();
    const hours12 = hours24 % 12 || 12;
    const period = hours24 < 12 ? 'AM' : 'PM';
    const minutes = padNumber(date.getMinutes());
    return (
      <span className="chat-date notranslate">
        <span className="chat-date-hours">{hours12}</span>
        <span className="chat-date-minutes">:{minutes}</span>
        <span className="chat-date-period"> {period}</span>
      </span>
    );
  }

  dateBreakFormatter = new Intl.DateTimeFormat('en', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  renderDateBreak(msg, date) {
    return (
      <li className="chat-date-break" key={`msg-${msg.id}-break`}>
        {this.dateBreakFormatter.format(date)}
      </li>
    );
  }

  renderMessageList(chatData) {
    let content = [];
    if (chatData && !chatData.loading && !chatData.error) {
      let lastCreated = null;
      chatData.chatMessages.forEach((msg, index) => {
        const created = fromIso9075DateString(msg.created);
        if (
          this.props.showTime &&
          (!lastCreated ||
            created.getFullYear() !== lastCreated.getFullYear() ||
            created.getMonth() !== lastCreated.getMonth() ||
            created.getDate() !== lastCreated.getDate())
        ) {
          content.push(this.renderDateBreak(msg, created));
        }
        content.push(this.renderChatMessage(msg, created));
        lastCreated = created;
      });
    }
    return content;
  }

  render() {
    const { gameData, title, chatData, participantData } = this.props;

    return (
      <div className="game-chat-container">
        {!!title && (
          <div className="chat-title" role="heading" aria-level="2">
            {title}
          </div>
        )}
        {gameData && (
          <div className="chat-header">
            <GamePlayerList gameData={gameData} />
          </div>
        )}
        {participantData && (
          <div className="chat-header">
            <ActiveParticipantDisplay participantData={participantData} />
          </div>
        )}
        <div
          className="chat-list-container"
          ref={div => {
            this.listContainer = div;
          }}
        >
          <ul className="chat-list">{this.renderMessageListMemo(chatData)}</ul>
        </div>
        <div className="feed-comment-container game-chat-form">
          <MessageForm
            onSubmit={this.handleSubmit}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onEmojiPicker={this.toggleEmojiPicker}
            message={this.state.message}
            placeholder={MESSAGE_TYPE_CHAT}
            showEmojiPicker={this.state.showEmojiPicker}
            addEmoji={this.addEmoji}
          />
        </div>
      </div>
    );
  }
}

const QUERY_GET_CHAT_MESSAGES = gql`
  query MyGetChatMessages($input: GetChatMessages!) {
    chatMessages(input: $input) {
      ...ChatMessageFields
    }
  }
  ${fragments.chatMessage}
`;

const MUTATION_MAKE_CHAT_MESSAGE = gql`
  mutation MyMakeChatMessage($input: MakeChatMessage!) {
    makeChatMessage(input: $input) {
      ...ChatMessageFields
    }
  }
  ${fragments.chatMessage}
`;

const SUBSCRIPTION_CHAT_UPDATES = gql`
  subscription MyChatUpdates($input: ChatUpdatesInput!) {
    chatUpdates(input: $input) {
      ...ChatMessageFields
    }
  }
  ${fragments.chatMessage}
`;

const withData = compose(
  graphql(QUERY_GET_CHAT_MESSAGES, {
    name: 'chatData',
    skip: props => !props.room,
    options: props => ({
      variables: {
        input: {
          room: props.room,
          count: 100,
          durationSec: props.historyDurationSec
        }
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps, chatData }) => ({
      chatData,
      subscribeToChatUpdates: () => {
        return chatData.subscribeToMore({
          document: SUBSCRIPTION_CHAT_UPDATES,
          variables: { input: { room: ownProps.room } },
          updateQuery: (prev, { subscriptionData: { data: newMsg } }) => {
            if (!newMsg || !newMsg.author || newMsg.room !== ownProps.room) {
              return null;
            }
            let result = cloneDeep(prev); // NOTE: Do not modify prev
            let modified = false;

            const msgs = result.chatMessages;
            if (insertionSortUnique(msgs, newMsg, m => m.id)) {
              modified = true;
              let overflow = msgs.length - MAX_CHAT_MESSAGES;
              if (overflow > 0) {
                msgs.splice(0, overflow);
              }
            }

            return modified ? result : null;
          }
        });
      }
    })
  }),
  graphql(MUTATION_MAKE_CHAT_MESSAGE, {
    props: ({ ownProps, mutate }) => ({
      makeChatMessage: message =>
        mutate({
          variables: {
            input: {
              room: ownProps.room,
              message: message
            }
          }
        })
    })
  }),
  getUserData({
    name: 'userData'
  })
);

const GameChatWithData = withData(GameChat);

GameChatWithData.defaultProps = {
  historyDurationSec: 3600,
  showTime: false
};

GameChatWithData.propTypes = {
  room: PropTypes.string.isRequired,
  historyDurationSec: PropTypes.number,
  showTime: PropTypes.bool,
  title: PropTypes.string,
  gameData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  }),
  chatData: PropTypes.shape({
    chatMessages: PropTypes.arrayOf(chatMessageShape),
    loading: PropTypes.bool,
    error: errorShape
  }),
  participantData: PropTypes.arrayOf(
    PropTypes.shape({
      user: PropTypes.shape({
        userId: PropTypes.string,
        displayName: PropTypes.string,
        avatarData: PropTypes.string
      })
    })
  )
};

export default GameChatWithData;
